import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import { KeyRound, ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import BackupHistory from './BackupHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

const SecuritySettings = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastRotation, setLastRotation] = useState(null);

  const handleRotate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/auth/rotate-jwt-secret`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Replace our token with the freshly-signed one so we stay logged in
      if (res.data?.token) localStorage.setItem('token', res.data.token);
      setLastRotation({
        rotated_at: res.data?.rotated_at,
        rotated_by: res.data?.rotated_by,
        secret_length: res.data?.secret_length,
      });
      toast.success('JWT secret rotated. All other sessions have been signed out.');
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Rotation failed';
      toast.error(typeof msg === 'string' ? msg : 'Rotation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="security-settings-section">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50 text-red-600">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <CardTitle>Security</CardTitle>
              <CardDescription>
                Sensitive credentials. Actions here may sign other users out.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 p-4 border rounded-lg bg-slate-50">
            <div className="flex items-start gap-3">
              <KeyRound className="w-5 h-5 text-slate-700 mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">JWT Signing Secret</div>
                <p className="text-sm text-slate-600 mt-1">
                  Generates a new 86-character random secret and replaces the one in
                  <code className="mx-1 px-1 py-0.5 rounded bg-slate-200 text-xs">/app/backend/.env</code>.
                  You will stay logged in (your token is re-issued). Every other active session
                  becomes invalid immediately.
                </p>
                {lastRotation && (
                  <p className="text-xs text-emerald-700 mt-2" data-testid="last-rotation-info">
                    Last rotated by <b>{lastRotation.rotated_by}</b> at {formatTime(lastRotation.rotated_at)} ({lastRotation.secret_length} chars).
                  </p>
                )}
              </div>
              <Button
                variant="destructive"
                onClick={() => setOpen(true)}
                data-testid="rotate-jwt-button"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Rotate JWT Secret
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
        <DialogContent data-testid="rotate-jwt-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              Rotate JWT Signing Secret?
            </DialogTitle>
            <DialogDescription>
              This will invalidate every active session except yours. All other admins
              and employees who are currently logged in will be silently signed out
              and need to log in again.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive" className="mt-2">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>This action cannot be undone.</AlertTitle>
            <AlertDescription>
              The old secret is overwritten in <code>backend/.env</code> and an
              entry is added to the audit log.
            </AlertDescription>
          </Alert>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              data-testid="rotate-jwt-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRotate}
              disabled={loading}
              data-testid="rotate-jwt-confirm-button"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {loading ? 'Rotating…' : 'Yes, rotate now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BackupHistory />
    </div>
  );
};

export default SecuritySettings;
